import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import Row, { ReflexRuleRow } from "./Row";
import { Loader } from "Shared/Common/Loader";
import NoRecord from "Shared/Common/NoRecord";
import PermissionComponent from "Shared/Common/Permissions/PermissionComponent";
import { ArrowDown, ArrowUp } from "Shared/Icons";
import { StringRecord } from "Shared/Type";
import usePagination from "Shared/hooks/usePagination";
import { sortingObject } from "Utils/consts";
import { reactSelectSMStyle, styles } from "Utils/Common";
import useLang from "Shared/hooks/useLanguage";
import useIsMobile from "Shared/hooks/useIsMobile";
import CustomPagination from "Shared/JsxPagination";
import PanelMappingService from "Services/InfectiousDisease/PanelMappingService";
import {
  GetAllPlatingRules,
  PanelsAgainstLabId,
} from "Services/OncologyCompendiumServices/PlatingRuleService";

const queryDisplayTagNames: StringRecord = {
  labId: "Lab",
  panelId: "Panel Name",
  ordersSelectedOnPanelForNY: "Number of Order selected On Panel for NY State",
  ordersSelectedOnPanel: "Number of Order selected On Panel",
};

export default function ReflexRule() {
  const { t } = useLang();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [triggerSearchData, setTriggerSearchData] = useState(false);

  const initialSearchQuery = {
    id: 0,
    panelId: 0,
    labId: 0,
    ordersSelectedOnPanelForNY: 0,
    ordersSelectedOnPanel: 0,
  };

  const {
    curPage,
    pageSize,
    total,
    totalPages,
    pageNumbers,
    nextPage,
    prevPage,
    showPage,
    setPageSize,
    setTotal,
    setCurPage,
  } = usePagination();

  const searchRef = useRef<any>(null);

  const [rows, setRows] = useState<ReflexRuleRow[]>(() => []);
  const [isAddButtonDisabled, setIsAddButtonDisabled] = useState(false);
  const [searchRequest, setSearchRequest] = useState(initialSearchQuery);
  const [sort, setSorting] = useState<any>({
    sortingOrder: "",
    clickedIconData: "",
  });

  function resetSearch() {
    setSearchRequest(initialSearchQuery);
    setSorting(sortingObject);
    setCurPage(1);
    setTriggerSearchData((prev) => !prev);
  }

  const gridQueryKey = useMemo(
    () => [
      "oncology-plating-rule",
      "grid",
      {
        pageNumber: curPage,
        pageSize,
        sortColumn: sort?.clickedIconData,
        sortDirection: sort?.sortingOrder,
        triggerSearchData,
        queryModel: searchRequest,
      },
    ],
    [
      curPage,
      pageSize,
      sort?.clickedIconData,
      sort?.sortingOrder,
      triggerSearchData,
      searchRequest,
    ]
  );

  const { data: gridData, isFetching: loading } = useQuery(
    gridQueryKey,
    async () => {
      const res = await GetAllPlatingRules({
        pageNumber: curPage,
        pageSize: pageSize,
        queryModel: searchRequest,
        sortColumn: sort?.clickedIconData,
        sortDirection: sort?.sortingOrder,
      });
      return (res as any)?.data;
    },
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      onError: (err: any) => console.trace(err, "err"),
    }
  );

  useEffect(() => {
    setTotal(gridData?.totalRecord ?? 0);
    setRows(gridData?.result ?? []);
  }, [gridData, setTotal]);

  const handleSort = (columnName: any) => {
    searchRef.current.id = searchRef.current.id
      ? searchRef.current.id === "asc"
        ? (searchRef.current.id = "desc")
        : (searchRef.current.id = "asc")
      : (searchRef.current.id = "asc");

    setSorting({
      sortingOrder: searchRef?.current?.id,
      clickedIconData: columnName,
    });
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      setCurPage(1);
      setTriggerSearchData((prev) => !prev);
    }
  };

  const { data: performingLabs } = useQuery(
    ["oncology-plating-rule", "performing-labs"],
    async () => {
      const res = await PanelMappingService.PerformingLabLookup();
      const raw = (res as any)?.data?.data ?? (res as any)?.data ?? [];
      const arr = Array.isArray(raw)
        ? raw
        : Array.isArray((raw as any)?.data)
          ? (raw as any).data
          : [];
      return arr.map((x: any) => ({
        value: x?.value ?? x?.labId ?? x?.referenceLabId ?? x?.id,
        label: x?.label ?? x?.labDisplayName ?? x?.referenceLabName ?? x?.name,
      }));
    },
    { staleTime: 10 * 60 * 1000 }
  );

  const { data: panelSearchList } = useQuery(
    ["oncology-plating-rule", "panels", null],
    async () => {
      const res = await PanelsAgainstLabId(null);
      const raw = (res as any)?.data?.data ?? (res as any)?.data ?? [];
      const arr = Array.isArray(raw)
        ? raw
        : Array.isArray((raw as any)?.data)
          ? (raw as any).data
          : [];
      return arr.map((x: any) => ({
        value: x?.value ?? x?.panelId ?? x?.id,
        label: x?.label ?? x?.panelDisplayName ?? x?.panelName ?? x?.name,
      }));
    },
    { staleTime: 10 * 60 * 1000 }
  );

  const saveMutation = useMutation(
    (payload: any) =>
      import("Services/OncologyCompendiumServices/PlatingRuleService").then(
        ({ SavePlatingRule }) => SavePlatingRule(payload)
      ),
    {
      onSuccess: (response: any) => {
        if (response?.data?.httpStatusCode === 200) {
          toast.success(response?.data?.message);
          queryClient.invalidateQueries(["oncology-plating-rule", "grid"]);
        } else {
          toast.error(response?.data?.message ?? t("Something went wrong."));
        }
      },
      onError: () => {
        toast.error(t("Something went wrong while saving data."));
      },
    }
  );

  // Handling searchedTags
  const [searchedTags, setSearchedTags] = useState<string[]>([]);

  const handleTagRemoval = (clickedTag: string) => {
    setSearchRequest((prevSearchRequest) => {
      return {
        ...prevSearchRequest,
        [clickedTag]: (initialSearchQuery as any)[clickedTag],
      };
    });
  };

  useEffect(() => {
    const uniqueKeys = new Set<string>();
    for (const [key, value] of Object.entries(searchRequest)) {
      if (value) {
        uniqueKeys.add(key);
      }
    }
    setSearchedTags(Array.from(uniqueKeys));
  }, [searchRequest]);

  useEffect(() => {
    if (searchedTags.length === 0) resetSearch();
  }, [searchedTags.length]);

  return (
    <>
      <div className="d-flex gap-4 flex-wrap mb-1">
        {searchedTags.map((tag) => (
          <div
            className="d-flex align-items-center cursor-pointer gap-1 p-2 rounded bg-light"
            onClick={() => handleTagRemoval(tag)}
            key={tag}
          >
            <span className="fw-bold">{t(queryDisplayTagNames[tag])}</span>
            <i className="bi bi-x"></i>
          </div>
        ))}
      </div>
      <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-sm-between align-items-center mb-2 col-12 responsive-flexed-actions">
        <div className="d-flex gap-2 responsive-flexed-actions">
          <div className="d-flex align-items-center">
            <span className="fw-400 mr-3">{t("Records")}</span>
            <select
              id={`IDCompendiumDataPharmDPreferenceRecords`}
              className="form-select w-sm-125px w-90px h-33px rounded py-2"
              data-kt-select2="true"
              data-placeholder="Select option"
              data-dropdown-parent="#kt_menu_63b2e70320b73"
              data-allow-clear="true"
              onChange={(e) => {
                setPageSize(parseInt(e.target.value));
                setCurPage(1);
              }}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="50" selected>
                50
              </option>
              <option value="100">100</option>
            </select>
          </div>
          <div className="d-flex gap-2 gap-lg-3 justify-content-center justify-content-sm-start">
            <div className="mt-0">
              <PermissionComponent
                moduleName="ID LIS"
                pageName="Compendium Data"
                permissionIdentifier="AddNew"
              >
                <Button
                  id={`IDCompendiumDataPharmDPreferenceAddRow`}
                  onClick={() => {
                    if (!isAddButtonDisabled) {
                      setRows((prevRows: any) => [
                        {
                          ...initialSearchQuery,
                          rowStatus: true,
                          id: 0,
                          panelId: "[]",
                          panelName: "",
                          panels: [],
                        },
                        ...prevRows,
                      ]);
                      setIsAddButtonDisabled(true);
                    }
                  }}
                  variant="contained"
                  color="success"
                  className="btn btn-primary btn-sm text-capitalize fw-400"
                  disabled={loading}
                  sx={{
                    "&.Mui-disabled": {
                      opacity: "0.65",
                      backgroundColor: "#69A54B",
                      color: "white",
                    },
                  }}
                >
                  <i className="bi bi-plus-lg"></i>
                  {t("Add Plating Rule")}
                </Button>
              </PermissionComponent>
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2 gap-lg-3">
          <button
            id={`IDCompendiumDataPharmDPreferenceSearch`}
            onClick={() => {
              setCurPage(1);
              setTriggerSearchData((prev) => !prev);
            }}
            className="btn btn-linkedin btn-sm fw-500"
            aria-controls="Search"
          >
            {t("Search")}
          </button>
          <button
            onClick={resetSearch}
            type="button"
            className="btn btn-secondary btn-sm btn-secondary--icon fw-500"
            id={`IDCompendiumDataPharmDPreferenceReset`}
          >
            <span>
              <span>{t("Reset")}</span>
            </span>
          </button>
        </div>
      </div>
      <div className="card">
        <Box sx={{ height: "auto", width: "100%" }}>
          <div className="table_bordered overflow-hidden">
            <TableContainer
              sx={
                isMobile
                  ? {}
                  : {
                      maxHeight: "calc(100vh - 100px)",
                      "&::-webkit-scrollbar": {
                        width: 7,
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "#fff",
                      },
                      "&:hover": {
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "var(--kt-gray-400)",
                          borderRadius: 2,
                        },
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "var(--kt-gray-400)",
                        borderRadius: 2,
                      },
                    }
              }
              className="shadow-none"
            >
              <Table
                aria-label="sticky table collapsible"
                className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
              >
                <TableHead>
                  <TableRow className="h-40px">
                    <TableCell></TableCell>
                    <TableCell>
                      <Select
                        inputId="IDCompendiumDataPanelMapingPerformingLab"
                        menuPortalTarget={document.body}
                        className="my-1"
                        theme={(theme) => styles(theme)}
                        placeholder={t("Select...")}
                        options={performingLabs ?? []}
                        styles={reactSelectSMStyle}
                        onChange={(event: any) => {
                          setSearchRequest({
                            ...searchRequest,
                            labId: event?.value ?? 0,
                          });
                        }}
                        value={(performingLabs ?? []).filter(
                          (option: any) => option.value === searchRequest?.labId
                        )}
                        onKeyDown={handleKeyPress}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        inputId="IDCompendiumDataPanelMapingPerformingLab"
                        menuPortalTarget={document.body}
                        className="my-1"
                        theme={(theme) => styles(theme)}
                        placeholder={t("Select...")}
                        options={panelSearchList ?? []}
                        styles={reactSelectSMStyle}
                        onChange={(event: any) => {
                          setSearchRequest({
                            ...searchRequest,
                            panelId: event?.value ?? 0,
                          });
                        }}
                        value={(panelSearchList ?? []).filter(
                          (option: any) =>
                            option.value === searchRequest?.panelId
                        )}
                        onKeyDown={handleKeyPress}
                      />
                    </TableCell>

                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                  <TableRow className="h-30px">
                    <TableCell className="min-w-50px">{t("Actions")}</TableCell>
                    <TableCell sx={{ width: "max-content" }}>
                      <div
                        onClick={() => handleSort("labId")}
                        className="d-flex justify-content-between cursor-pointer"
                        id=""
                        ref={searchRef}
                      >
                        <div style={{ width: "max-content" }}>
                          {t("Lab Name")}
                        </div>

                        <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                          <ArrowUp
                            CustomeClass={`${
                              sort.sortingOrder === "desc" &&
                              sort.clickedIconData === "labId"
                                ? "text-success fs-7"
                                : "text-gray-700 fs-7"
                            }  p-0 m-0 "`}
                          />
                          <ArrowDown
                            CustomeClass={`${
                              sort.sortingOrder === "asc" &&
                              sort.clickedIconData === "labId"
                                ? "text-success fs-7"
                                : "text-gray-700 fs-7"
                            }  p-0 m-0`}
                          />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell sx={{ width: "max-content" }}>
                      <div
                        onClick={() => handleSort("panelName")}
                        className="d-flex justify-content-between cursor-pointer"
                        id=""
                        ref={searchRef}
                      >
                        <div style={{ width: "max-content" }}>{t("Panel")}</div>

                        <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                          <ArrowUp
                            CustomeClass={`${
                              sort.sortingOrder === "desc" &&
                              sort.clickedIconData === "panelName"
                                ? "text-success fs-7"
                                : "text-gray-700 fs-7"
                            }  p-0 m-0 "`}
                          />
                          <ArrowDown
                            CustomeClass={`${
                              sort.sortingOrder === "asc" &&
                              sort.clickedIconData === "panelName"
                                ? "text-success fs-7"
                                : "text-gray-700 fs-7"
                            }  p-0 m-0`}
                          />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell sx={{ width: "max-content" }}>
                      <div
                        onClick={() => handleSort("reflexRule")}
                        className="d-flex justify-content-between cursor-pointer"
                        id=""
                        ref={searchRef}
                      >
                        <div style={{ width: "max-content" }}>
                          {t("Number of Order selected On Panel for NY State")}
                        </div>

                        <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                          <ArrowUp
                            CustomeClass={`${
                              sort.sortingOrder === "desc" &&
                              sort.clickedIconData === "reflexRule"
                                ? "text-success fs-7"
                                : "text-gray-700 fs-7"
                            }  p-0 m-0 "`}
                          />
                          <ArrowDown
                            CustomeClass={`${
                              sort.sortingOrder === "asc" &&
                              sort.clickedIconData === "reflexRule"
                                ? "text-success fs-7"
                                : "text-gray-700 fs-7"
                            }  p-0 m-0`}
                          />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="min-w-50px">
                      <div style={{ width: "max-content" }}>
                        {t("Number of Order selected On Panel")}
                      </div>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableCell colSpan={7} className="">
                      <Loader />
                    </TableCell>
                  ) : rows.length ? (
                    rows.map((item: any, index) => {
                      return (
                        <Row
                          row={item}
                          rows={rows}
                          key={item.id}
                          index={index}
                          setRows={setRows}
                          performingLabOptions={performingLabs ?? []}
                          queryDisplayTagNames={queryDisplayTagNames}
                          setIsAddButtonDisabled={setIsAddButtonDisabled}
                          onSave={saveMutation.mutateAsync}
                        />
                      );
                    })
                  ) : (
                    <NoRecord colSpan={7} />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </Box>
      </div>
      <div className="card">
        <Box sx={{ height: "auto", width: "100%" }}>
          <CustomPagination
            curPage={curPage}
            nextPage={nextPage}
            pageNumbers={pageNumbers}
            pageSize={pageSize}
            prevPage={prevPage}
            showPage={showPage}
            total={total}
            totalPages={totalPages}
          />
        </Box>
      </div>
    </>
  );
}
